import { alertVariants } from "@visiblethread/react-components";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import {
  setIsLocalFileSharingEnabled,
  setIsRagEnabled,
} from "slices/generativeAi/generativeAiSlice";

import localhost, { downloadFileFromResponse } from "../../apis/localhost";
import { CHUNK_SIZE } from "../../components/Modal/ChooseFilesModal/utils";
import { modalTypes } from "../../constants/types";
import { FILE_SOURCES } from "../../layout/documentUploader/constants";
import { showDownloadErrorModal, showErrorModal } from "../app/actions/error";
import { hideModal, showModal } from "../app/actions/modals";
import {
  addToJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "../app/appSlice";
import { startSSEConnection } from "../workspace/actions/uploadFiles";
import {
  cleanupFiles,
  clearFileUploadStates,
  initFileUploadStates,
} from "../workspace/foldersSlice";
import {
  removeCollection,
  setList,
  setSelected,
  setUsers,
} from "./collectionsSlice";
import { selectCollections, selectSelectedCollection } from "./selectors";

const RAG_COLLECTION = "rag/collection";

export const formatFiles = (files) => {
  return files.map(
    ({
      name,
      source,
      status,
      hash,
      id,
      ragCollectionId,
      addedOn,
      fileUploadId,
      sharepointSitePath,
      sharepointHost,
      sharepointFilePathFromSite,
    }) => {
      const path = sharepointFilePathFromSite;
      const route = path?.substring(0, path.lastIndexOf("/"));
      return {
        source: FILE_SOURCES[source],
        content: {
          name,
          sitePath: sharepointSitePath,
          host: sharepointHost,
          filePath: route,
        },
        hash,
        status,
        id,
        ragCollectionId,
        addedOn,
        fileUploadId,
      };
    }
  );
};

const formatCollection = ({
  name,
  documents,
  id,
  description,
  workspaceUserId,
  status,
  addedOn,
  lastUpdated,
  creator,
  isShared,
  shares,
  sharedOn,
  isOwner,
  collectionCategories,
}) => {
  return {
    name,
    files: formatFiles(documents),
    creator,
    id,
    description,
    workspaceUserId,
    status,
    addedOn,
    lastUpdated,
    isShared,
    shares: shares || [],
    sharedOn,
    isOwner,
    collectionCategories: collectionCategories || [],
  };
};

const formatCollections = (collections) => {
  return collections.map(formatCollection);
};

export const createCollection = (name) => async (dispatch) => {
  dispatch(addToJobQueue("createCollection"));

  try {
    const response = await localhost.post(RAG_COLLECTION, {
      name,
      files: [],
    });

    const { collection } = response.data;
    dispatch(getCollections());
    dispatch(setSelected(formatCollection(collection)));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: `Collection "${collection.name}" was created successfully.`,
      })
    );
  } catch (error) {
    dispatch(showErrorModal(error, "Failed to create the collection."));
  } finally {
    dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
    dispatch(removeFromJobQueue("createCollection"));
  }
};

export const updateCollection = (rawCollection) => (dispatch, getState) => {
  if (!rawCollection) {
    return;
  }

  const [formatted] = formatCollections([rawCollection]);

  const list = getState()?.collections?.list || [];

  // Remove existing collection (by id or placeholder without id)
  const filtered = list.filter((c) => c?.id && c.id !== formatted.id);

  // Add updated collection to the first position
  dispatch(setList([formatted, ...filtered]));

  return formatted;
};

export const getCollections = () => async (dispatch) => {
  dispatch(addToJobQueue("getCollections"));

  try {
    const response = await localhost.get(RAG_COLLECTION);
    dispatch(setList(formatCollections(response.data.collections)));
    dispatch(setUsers(response.data.users));
    dispatch(setIsRagEnabled(response?.data?.collectionsEnabled ?? false));
    dispatch(
      setIsLocalFileSharingEnabled(
        response?.data?.collectionSharingEnabled ?? false
      )
    );
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("getCollections"));
  }
};

export const deleteCollection =
  (collectionId) => async (dispatch, getState) => {
    dispatch(addToJobQueue("deleteCollection"));
    try {
      const response = await localhost.delete(RAG_COLLECTION, {
        params: { collectionId },
      });
      if (response.status === 200) {
        const state = getState();
        const deletedCollection = selectCollections(state).find(
          (c) => c.id === collectionId
        );
        const selectedCollection = selectSelectedCollection(state);

        dispatch(removeCollection(collectionId));
        dispatch(
          showGlobalAlert({
            text: `Collection "${deletedCollection?.name}" was successfully deleted.`,
            variant: alertVariants.success,
          })
        );
        if (collectionId === selectedCollection?.id) {
          dispatch(setSelected(null));
        }
      }
    } catch (error) {
      dispatch(showErrorModal(error));
    } finally {
      dispatch(removeFromJobQueue("deleteCollection"));
    }
  };

export const saveCollection = (collection) => async (dispatch) => {
  dispatch(addToJobQueue("saveCollection"));
  try {
    const { data } = await localhost.post("rag/collection/reindex", {
      ...collection,
      name: collection.name.trim(),
      files: collection.files.map((file) => ({
        name: file.content.name,
        size: file.content.size,
        hash: file.hash,
        totalChunks: Math.ceil(file.content.size / CHUNK_SIZE),
        source: "LOCAL",
        ragDocumentId: file.id,
      })),
    });
    dispatch(updateCollection(data.collection));
    if (
      data.filesToBeReindexed?.length > 0 ||
      data.filesToBeUploadedFromLocal?.length > 0
    ) {
      dispatch(
        showModal(modalTypes.UPLOAD_FILES, {
          target: UPLOAD_ENTITY_TYPES.COLLECTIONS,
          collection: collection,
        })
      );
    }
  } catch (error) {
    dispatch(showErrorModal(error));
    throw error;
  } finally {
    dispatch(removeFromJobQueue("saveCollection"));
  }
};

export const saveCollectionShares = (collection) => async (dispatch) => {
  dispatch(addToJobQueue("saveCollectionShares"));
  try {
    return await localhost.post("rag/collection/share", {
      collectionId: collection.id,
      workspaceUserIds: collection.shares,
    });
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("saveCollectionShares"));
  }
};

/**
 * Builds the reindex payload for a single collection.
 * Handles both LOCAL and SHAREPOINT file sources.
 */
const buildReindexPayload = (collection, flowId) => ({
  id: collection.id,
  name: collection.name,
  ...(flowId && { flowId }),
  files: collection.files.map((file) => {
    const isSharepoint = file.source === FILE_SOURCES.SHAREPOINT;
    return {
      name: file.content.name,
      size: file.content.size,
      hash: file.hash,
      ragDocumentId: file.id,
      source: isSharepoint ? "SHAREPOINT" : "LOCAL",
      ...(isSharepoint && {
        sharepointFilePathFromSite: `${file.content.filePath ?? file.content.path}/${file.content.name}`,
        sharepointSitePath: file.content.sitePath,
        sharepointHost: file.content.host,
      }),
      ...(!isSharepoint && {
        totalChunks: Math.ceil((file.content.size || 0) / CHUNK_SIZE),
      }),
    };
  }),
});

/**
 * Calls the reindex endpoint for a single collection.
 * Returns the response data including files that need processing.
 */
const reindexCollection = async (collection, flowId) => {
  const payload = buildReindexPayload(collection, flowId);
  const response = await localhost.post("rag/collection/reindex", payload);
  return {
    collection,
    data: response.data,
  };
};

/**
 * Verifies that documents in multiple collections are indexed in the RAG system.
 * Calls the reindex endpoint for each collection - the backend's intelligent
 * re-indexing will skip already-indexed documents.
 *
 * This action handles:
 * - SSE connection for progress tracking
 * - Parallel reindex calls for all selected collections
 * - Showing the upload modal if any documents need processing
 *
 * @param {Array} collections - Array of collection objects to verify/reindex
 * @returns {Promise<boolean>} - True if any processing is needed, false otherwise
 */
export const verifyCollectionsIndexed =
  (collections) => async (dispatch, _getState) => {
    if (!collections || collections.length === 0) {
      return false;
    }

    dispatch(addToJobQueue("verifyCollectionsIndexed"));

    try {
      // Aggregate all files from all collections for SSE tracking
      const allFiles = collections.flatMap((c) => c.files || []);

      // Remove duplicate files (same hash) across collections
      const uniqueFiles = allFiles.filter(
        (file, index, self) =>
          index === self.findIndex((f) => f.hash === file.hash)
      );

      // Initialize file upload states for SSE progress tracking
      dispatch(initFileUploadStates(uniqueFiles));

      // Generate a shared flowId for SSE progress tracking across all collections
      const flowId = `collection-reindex-${Date.now()}-${crypto.randomUUID()}`;

      // Start SSE connection for real-time progress updates
      await dispatch(startSSEConnection(flowId));

      // Call reindex for each collection in parallel
      // The backend will skip already-indexed documents (intelligent re-indexing)
      const results = await Promise.all(
        collections.map((collection) => reindexCollection(collection, flowId))
      );

      // Trigger SSE processing after all AWAITING_SSE records are created in the DB
      try {
        await localhost.post("/document/queue/stream/start-processing", {
          flowId,
        });
      } catch {
        try {
          await localhost.post("/document/queue/stream/start-processing", {
            flowId,
          });
        } catch (retryError) {
          dispatch(
            showErrorModal(
              retryError,
              "Failed to start processing. Please try again."
            )
          );
          return false;
        }
      }

      // Update each collection in the store with response data
      results.forEach(({ data }) => {
        if (data?.collection) {
          dispatch(updateCollection(data.collection));
        }
      });

      // Aggregate files that need processing from all collections
      const allFilesToProcess = results.flatMap(
        ({ data }) => data?.filesToBeProcessed || []
      );
      const allFilesToUpload = results.flatMap(
        ({ data }) => data?.filesToBeUploadedFromLocal || []
      );

      const hasFilesToProcess =
        allFilesToProcess.length > 0 || allFilesToUpload.length > 0;

      if (!hasFilesToProcess) {
        // No files need processing — clear the WAITING states that
        // initFileUploadStates wrote, otherwise they orphan in Redux
        // and selectHasInProgressUpload stays true, disabling the
        // "Add Document" button on DocumentUploader.
        dispatch(clearFileUploadStates());
        return false;
      }

      // Create a combined collection for display in the upload modal
      const combinedCollection = {
        id: collections.length === 1 ? collections[0].id : null,
        name:
          collections.length === 1
            ? collections[0].name
            : `${collections.length} Collections`,
        files: uniqueFiles,
      };

      // Set combined collection for UploadFilesModal compatibility
      dispatch(setSelected(combinedCollection));

      // Get files that need to be displayed in the progress modal
      const displayedFiles = uniqueFiles.filter(
        (file) =>
          allFilesToProcess.includes(file.content.name) ||
          allFilesToUpload.includes(file.content.name)
      );

      // Clean up file states to only track files being processed
      dispatch(cleanupFiles(displayedFiles));

      // Close collections modal before showing upload modal
      dispatch(hideModal(modalTypes.COLLECTIONS_MODAL));

      // Show upload progress modal
      dispatch(
        showModal(modalTypes.UPLOAD_FILES, {
          target: UPLOAD_ENTITY_TYPES.COLLECTIONS,
          displayedFiles,
          onFilesUploaded: () => {
            // Clear selected collection after upload completes
            dispatch(setSelected(null));
          },
        })
      );

      return true;
    } catch (error) {
      dispatch(clearFileUploadStates());
      dispatch(showErrorModal(error, "Failed to verify collection indexing."));
      return false;
    } finally {
      dispatch(removeFromJobQueue("verifyCollectionsIndexed"));
    }
  };

export const downloadCollectionDocument = (id, name) => async (dispatch) => {
  dispatch(addToJobQueue("downloadCollectionDocument"));
  try {
    const { data } = await localhost.get(`/rag/document/${id}/download`, {
      responseType: "blob",
      skipAuthRedirect: true,
    });
    downloadFileFromResponse(data, name);
  } catch (error) {
    dispatch(showDownloadErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("downloadCollectionDocument"));
  }
};
